import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const ArtistSplashSlideshow = () => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const transitionRef = useRef(null);

  useEffect(() => {
    const fetchArtistShowcase = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch active artists with splash images from artist_promotions
        const { data: promotions, error: fetchError } = await supabase
          ?.from('artist_promotions')
          ?.select(`
            id,
            splash_image_path,
            user_id,
            user_profiles!inner (
              full_name,
              artist_type,
              status,
              role
            )
          `)
          ?.not('splash_image_path', 'is', null)
          ?.not('splash_image_path', 'eq', '')
          ?.eq('user_profiles.role', 'artist')
          ?.eq('user_profiles.status', 'active');

        if (fetchError) {
          console.warn('Showcase fetch error:', fetchError);
          setError('Unable to load artist showcase');
          return;
        }

        if (!promotions || promotions?.length === 0) {
          setError('No artist showcase available');
          return;
        }

        // Build public URLs — bucket is now public so no signed URL needed
        const validSlides = promotions
          ?.map((promo) => {
            if (!promo?.splash_image_path) return null;

            const { data: urlData } = supabase
              ?.storage
              ?.from('artist-promotion-assets')
              ?.getPublicUrl(promo?.splash_image_path);

            if (!urlData?.publicUrl) return null;

            return {
              id: promo?.id,
              imageUrl: urlData?.publicUrl,
              artistName: promo?.user_profiles?.full_name || 'Unknown Artist',
              artistType: promo?.user_profiles?.artist_type || 'Artist',
            };
          })
          ?.filter(Boolean);

        if (validSlides?.length === 0) {
          setError('No valid artist images found');
          return;
        }

        setSlides(validSlides);
      } catch (err) {
        console.error('Error loading artist showcase:', err);
        setError('Loading artist showcase...');
      } finally {
        setLoading(false);
      }
    };

    fetchArtistShowcase();
  }, []);

  // Dissolving rotation — 4 seconds per slide, 1 second dissolve
  useEffect(() => {
    if (!slides || slides?.length <= 1) return;

    const advance = () => {
      const next = (currentIndex + 1) % slides?.length;
      setNextIndex(next);
      setTransitioning(true);

      transitionRef.current = setTimeout(() => {
        setCurrentIndex(next);
        setNextIndex(null);
        setTransitioning(false);
      }, 1000); // dissolve duration
    };

    timerRef.current = setInterval(advance, 4000); // 4 seconds per slide

    return () => {
      clearInterval(timerRef?.current);
      clearTimeout(transitionRef?.current);
    };
  }, [slides, currentIndex]);

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-800">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg font-medium">Loading Artist Showcase...</p>
        </div>
      </div>
    );
  }

  if (error || !slides || slides?.length === 0) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%)' }}
      >
        <div className="text-center text-white max-w-md px-6">
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4">ID Artists</h1>
          <p className="text-xl text-white/90 mb-2">Artist Showcase</p>
          <p className="text-lg text-white/75">Discover Creative Talent</p>
        </div>
      </div>
    );
  }

  const current = slides?.[currentIndex];
  const next = nextIndex !== null ? slides?.[nextIndex] : null;

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Current slide */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${current?.imageUrl})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />
      </div>
      {/* Next slide — fades in during transition */}
      {next && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${next?.imageUrl})`,
            opacity: transitioning ? 1 : 0,
            transition: 'opacity 1000ms ease-in-out',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />
        </div>
      )}
      {/* Artist info overlay — always shows current artist, fades during transition */}
      <div
        className="absolute bottom-0 left-0 right-0 p-8"
        style={{
          opacity: transitioning ? 0 : 1,
          transition: 'opacity 400ms ease-in-out',
        }}
      >
        <div className="text-white">
          <h3 className="text-3xl font-bold mb-1 drop-shadow-lg">{current?.artistName}</h3>
          <p className="text-white/80 text-lg capitalize drop-shadow-md">
            {current?.artistType}
          </p>
        </div>
      </div>
      {/* Dot indicators */}
      {slides?.length > 1 && (
        <div className="absolute bottom-6 right-6 flex space-x-2">
          {slides?.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                clearInterval(timerRef?.current);
                clearTimeout(transitionRef?.current);
                setCurrentIndex(idx);
                setNextIndex(null);
                setTransitioning(false);
              }}
              className={`rounded-full transition-all duration-300 ${
                idx === currentIndex
                  ? 'w-6 h-2 bg-white' :'w-2 h-2 bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArtistSplashSlideshow;